import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Upload,
} from "antd";
import { DeleteOutlined, EditOutlined, HolderOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ImgCrop from "antd-img-crop";
import { useProductColors } from "../../../../data/hooks/useProductColors";
import { useProductMaterials } from "../../../../data/hooks/useProductMaterials";
import { useProductColorShades } from "../../../../data/hooks/useProductColorShades";
import { useProductMaterialGroups } from "../../../../data/hooks/useProductMaterialGroups";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import firebase from "../../../../configs/FirebaseConfig";
import {
  FIRESTORE_PRODUCT_COLOR_SHADES_TABLE,
  FIRESTORE_PRODUCT_MATERIAL_GROUPS_TABLE,
} from "../../../../constants/FirestoreConstant";

const { TabPane } = Tabs;

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const DraggableRow = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });
  const style = Object.assign(
    Object.assign(Object.assign({}, props.style), {
      transform: CSS.Translate.toString(transform),
      transition,
    }),
    isDragging ? { position: "relative", zIndex: 9999 } : {},
  );
  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

export default function ProductAttributes() {
  const { t } = useTranslation();
  const firestore = firebase.firestore();
  const { colors, addColor, updateColor, deleteColor, isLoading: colorsLoading } = useProductColors();
  const { materials, addMaterial, updateMaterial, deleteMaterial, isLoading: materialsLoading } = useProductMaterials();
  const {
    colorShades,
    addColorShade,
    updateColorShade,
    deleteColorShade,
    isLoading: shadesLoading,
  } = useProductColorShades();
  const {
    materialGroups,
    addMaterialGroup,
    updateMaterialGroup,
    deleteMaterialGroup,
    isLoading: groupsLoading,
  } = useProductMaterialGroups();

  const [shadesList, setShadesList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [saveShadeSortingDisabled, setSaveShadeSortingDisabled] = useState(true);
  const [saveGroupSortingDisabled, setSaveGroupSortingDisabled] = useState(true);
  const [shadeSortLoading, setShadeSortLoading] = useState(false);
  const [groupSortLoading, setGroupSortLoading] = useState(false);

  const [colorForm] = Form.useForm();
  const [materialForm] = Form.useForm();
  const [editColorForm] = Form.useForm();
  const [editMaterialForm] = Form.useForm();
  const [shadeForm] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [editShadeForm] = Form.useForm();
  const [editGroupForm] = Form.useForm();

  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [editColorModalOpen, setEditColorModalOpen] = useState(false);
  const [editMaterialModalOpen, setEditMaterialModalOpen] = useState(false);
  const [shadeModalOpen, setShadeModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editShadeModalOpen, setEditShadeModalOpen] = useState(false);
  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);

  const [currentColor, setCurrentColor] = useState(null);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [currentShade, setCurrentShade] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);

  const [shadeImageFile, setShadeImageFile] = useState(null);
  const [editShadeImageFile, setEditShadeImageFile] = useState(null);

  const shadeImageUploadProps = {
    name: "file",
    listType: "picture-card",
    maxCount: 1,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
      if (!isJpgOrPng) {
        messageApi.error("Afbeeldingen moeten Webp, PNG of JPG bestanden zijn");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        messageApi.error("Afbeeldingen mogen maximaal 2MB zijn");
        return Upload.LIST_IGNORE;
      }
      setShadeImageFile(file);
      return false;
    },
    onRemove: () => {
      setShadeImageFile(null);
    },
  };

  const getEditShadeImageUploadProps = () => ({
    name: "file",
    listType: "picture-card",
    maxCount: 1,
    defaultFileList: currentShade?.image
      ? [
          {
            uid: "-1",
            name: "current-image",
            status: "done",
            url: currentShade.image,
          },
        ]
      : [],
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
      if (!isJpgOrPng) {
        messageApi.error("Afbeeldingen moeten Webp, PNG of JPG bestanden zijn");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        messageApi.error("Afbeeldingen mogen maximaal 2MB zijn");
        return Upload.LIST_IGNORE;
      }
      setEditShadeImageFile(file);
      return false;
    },
    onRemove: () => {
      setEditShadeImageFile(null);
    },
  });

  useEffect(() => {
    if (colorShades) {
      setShadesList(colorShades.map((shade) => ({ ...shade, key: shade.sortOrder || 999 })));
    }
  }, [colorShades]);

  useEffect(() => {
    if (materialGroups) {
      setGroupsList(materialGroups.map((group) => ({ ...group, key: group.sortOrder || 999 })));
    }
  }, [materialGroups]);

  const [messageApi, contextHolder] = message.useMessage();

  const handleCreateColor = () => {
    colorForm.validateFields().then(async (values) => {
      try {
        await addColor({
          name: values.name,
          code: values.code.toUpperCase(),
          shadeId: values.shadeId,
          active: true,
        });
        setColorModalOpen(false);
        colorForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateColor = () => {
    editColorForm.validateFields().then(async (values) => {
      try {
        await updateColor(currentColor.id, {
          name: values.name,
          code: values.code.toUpperCase(),
          shadeId: values.shadeId,
        });
        setEditColorModalOpen(false);
        setCurrentColor(null);
        editColorForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteColor = async (color) => {
    try {
      await deleteColor(color.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const handleCreateMaterial = () => {
    materialForm.validateFields().then(async (values) => {
      try {
        await addMaterial({
          name: values.name,
          code: values.code.toUpperCase(),
          groupId: values.groupId,
          description: values.description || null,
          active: true,
        });
        setMaterialModalOpen(false);
        materialForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateMaterial = () => {
    editMaterialForm.validateFields().then(async (values) => {
      try {
        await updateMaterial(currentMaterial.id, {
          name: values.name,
          code: values.code.toUpperCase(),
          groupId: values.groupId,
          description: values.description || null,
        });
        setEditMaterialModalOpen(false);
        setCurrentMaterial(null);
        editMaterialForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteMaterial = async (material) => {
    try {
      await deleteMaterial(material.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const openEditColorModal = (color) => {
    setCurrentColor(color);
    editColorForm.setFieldsValue({
      name: color.name,
      code: color.code,
      shadeId: color.shadeId,
    });
    setEditColorModalOpen(true);
  };

  const openEditMaterialModal = (material) => {
    setCurrentMaterial(material);
    editMaterialForm.setFieldsValue({
      name: material.name,
      code: material.code,
      groupId: material.groupId,
      description: material.description,
    });
    setEditMaterialModalOpen(true);
  };

  const handleCreateShade = () => {
    shadeForm.validateFields().then(async (values) => {
      try {
        await addColorShade({
          name: values.name,
          nameEN: values.nameEN,
          nameDE: values.nameDE,
          code: values.code.toUpperCase(),
          imageFile: shadeImageFile,
          sortOrder: values.sortOrder || shadesList.length,
          active: true,
        });
        setShadeModalOpen(false);
        shadeForm.resetFields();
        setShadeImageFile(null);
        setEditShadeImageFile(null);
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateShade = () => {
    editShadeForm.validateFields().then(async (values) => {
      try {
        await updateColorShade(currentShade.id, {
          name: values.name,
          nameEN: values.nameEN,
          nameDE: values.nameDE,
          code: values.code.toUpperCase(),
          imageFile: editShadeImageFile,
          sortOrder: values.sortOrder,
        });
        setEditShadeModalOpen(false);
        setCurrentShade(null);
        editShadeForm.resetFields();
        setEditShadeImageFile(null);
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteShade = async (shade) => {
    try {
      await deleteColorShade(shade.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const openEditShadeModal = (shade) => {
    setCurrentShade(shade);
    setEditShadeImageFile(null);
    editShadeForm.setFieldsValue({
      name: shade.name,
      nameEN: shade.nameEN,
      nameDE: shade.nameDE,
      code: shade.code,
      sortOrder: shade.sortOrder,
    });
    setEditShadeModalOpen(true);
  };

  const handleCreateGroup = () => {
    groupForm.validateFields().then(async (values) => {
      try {
        await addMaterialGroup({
          name: values.name,
          nameEN: values.nameEN,
          nameDE: values.nameDE,
          code: values.code.toUpperCase(),
          sortOrder: values.sortOrder || groupsList.length,
          active: true,
        });
        setGroupModalOpen(false);
        groupForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateGroup = () => {
    editGroupForm.validateFields().then(async (values) => {
      try {
        await updateMaterialGroup(currentGroup.id, {
          name: values.name,
          nameEN: values.nameEN,
          nameDE: values.nameDE,
          code: values.code.toUpperCase(),
          sortOrder: values.sortOrder,
        });
        setEditGroupModalOpen(false);
        setCurrentGroup(null);
        editGroupForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteGroup = async (group) => {
    try {
      await deleteMaterialGroup(group.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const openEditGroupModal = (group) => {
    setCurrentGroup(group);
    editGroupForm.setFieldsValue({
      name: group.name,
      nameEN: group.nameEN,
      nameDE: group.nameDE,
      code: group.code,
      sortOrder: group.sortOrder,
    });
    setEditGroupModalOpen(true);
  };

  const saveShadeSortingOrder = async () => {
    setShadeSortLoading(true);
    const batch = firestore.batch();
    const updatedList = shadesList.map((item, index) => ({ ...item, key: index, sortOrder: index }));
    updatedList.forEach((item) => {
      const docRef = firestore.collection(FIRESTORE_PRODUCT_COLOR_SHADES_TABLE).doc(item.id);
      batch.update(docRef, { sortOrder: item.sortOrder });
    });
    await batch.commit();
    setShadesList(updatedList);
    setShadeSortLoading(false);
    setSaveShadeSortingDisabled(true);
  };

  const saveGroupSortingOrder = async () => {
    setGroupSortLoading(true);
    const batch = firestore.batch();
    const updatedList = groupsList.map((item, index) => ({ ...item, key: index, sortOrder: index }));
    updatedList.forEach((item) => {
      const docRef = firestore.collection(FIRESTORE_PRODUCT_MATERIAL_GROUPS_TABLE).doc(item.id);
      batch.update(docRef, { sortOrder: item.sortOrder });
    });
    await batch.commit();
    setGroupsList(updatedList);
    setGroupSortLoading(false);
    setSaveGroupSortingDisabled(true);
  };

  const onShadeDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setSaveShadeSortingDisabled(false);
      setShadesList((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.key === active?.id);
        const overIndex = prevState.findIndex((record) => record.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  const onGroupDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setSaveGroupSortingDisabled(false);
      setGroupsList((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.key === active?.id);
        const overIndex = prevState.findIndex((record) => record.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  const colorColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: "ascend",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("color_shade"),
      dataIndex: "shadeId",
      key: "shadeId",
      render: (shadeId, record) => (
        <Select
          value={shadeId}
          placeholder={t("form.choose_color_shade")}
          style={{ width: 150 }}
          onChange={(value) => updateColor(record.id, { shadeId: value })}
          allowClear
          options={[...(colorShades || [])]
            .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
            .map((shade) => ({
              value: shade.id,
              label: shade.name,
            }))}
        />
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditColorModal(record)} />
          <Popconfirm
            title={t("confirm_delete_color")}
            onConfirm={() => handleDeleteColor(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const materialColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: "ascend",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("material_group"),
      dataIndex: "groupId",
      key: "groupId",
      render: (groupId, record) => (
        <Select
          value={groupId}
          placeholder={t("form.choose_material_group")}
          style={{ width: 150 }}
          onChange={(value) => updateMaterial(record.id, { groupId: value })}
          allowClear
          options={[...(materialGroups || [])]
            .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
            .map((group) => ({
              value: group.id,
              label: group.name,
            }))}
        />
      ),
    },
    {
      title: t("description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditMaterialModal(record)} />
          <Popconfirm
            title={t("confirm_delete_material")}
            onConfirm={() => handleDeleteMaterial(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const shadeColumns = [
    { key: "sort", align: "center", width: 80, render: () => <DragHandle /> },
    {
      title: t("image"),
      dataIndex: "image",
      key: "image",
      width: 80,
      render: (image) =>
        image ? (
          <img src={image} alt="shade" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} />
        ) : (
          <div style={{ width: 50, height: 50, background: "#f0f0f0", borderRadius: 4 }} />
        ),
    },
    {
      title: t("name_nl"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => <a onClick={() => openEditShadeModal(record)}>{record.name}</a>,
    },
    {
      title: t("name_en"),
      dataIndex: "nameEN",
      key: "nameEN",
    },
    {
      title: t("name_de"),
      dataIndex: "nameDE",
      key: "nameDE",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title={t("confirm_delete_shade")}
            onConfirm={() => handleDeleteShade(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const groupColumns = [
    { key: "sort", align: "center", width: 80, render: () => <DragHandle /> },
    {
      title: t("name_nl"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => <a onClick={() => openEditGroupModal(record)}>{record.name}</a>,
    },
    {
      title: t("name_en"),
      dataIndex: "nameEN",
      key: "nameEN",
    },
    {
      title: t("name_de"),
      dataIndex: "nameDE",
      key: "nameDE",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title={t("confirm_delete_group")}
            onConfirm={() => handleDeleteGroup(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <Tabs defaultActiveKey="shades">
          <TabPane tab={t("color_shades")} key="shades">
            <Space className="mb-2 d-flex justify-content-between">
              <div />
              <div style={{ display: "flex", gap: 8 }}>
                <Button disabled={saveShadeSortingDisabled} onClick={saveShadeSortingOrder}>
                  {t("save_sorting_order")}
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShadeModalOpen(true)}>
                  {t("add_color_shade")}
                </Button>
              </div>
            </Space>
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onShadeDragEnd}>
              <SortableContext items={shadesList.map((i) => i.key)} strategy={verticalListSortingStrategy}>
                <Table
                  loading={shadeSortLoading}
                  rowKey="key"
                  components={{ body: { row: DraggableRow } }}
                  columns={shadeColumns}
                  dataSource={shadesList}
                  pagination={{ pageSize: 20 }}
                />
              </SortableContext>
            </DndContext>
          </TabPane>
          <TabPane tab={t("colors")} key="colors">
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setColorModalOpen(true)}>
                {t("add_color")}
              </Button>
            </div>
            <Table columns={colorColumns} dataSource={colors || []} rowKey="id" pagination={{ pageSize: 20 }} />
          </TabPane>
          <TabPane tab={t("material_groups")} key="groups">
            <Space className="mb-2 d-flex justify-content-between">
              <div />
              <div style={{ display: "flex", gap: 8 }}>
                <Button disabled={saveGroupSortingDisabled} onClick={saveGroupSortingOrder}>
                  {t("save_sorting_order")}
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setGroupModalOpen(true)}>
                  {t("add_material_group")}
                </Button>
              </div>
            </Space>
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onGroupDragEnd}>
              <SortableContext items={groupsList.map((i) => i.key)} strategy={verticalListSortingStrategy}>
                <Table
                  loading={groupSortLoading}
                  rowKey="key"
                  components={{ body: { row: DraggableRow } }}
                  columns={groupColumns}
                  dataSource={groupsList}
                  pagination={{ pageSize: 20 }}
                />
              </SortableContext>
            </DndContext>
          </TabPane>
          <TabPane tab={t("materials")} key="materials">
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setMaterialModalOpen(true)}>
                {t("add_material")}
              </Button>
            </div>
            <Table columns={materialColumns} dataSource={materials || []} rowKey="id" pagination={{ pageSize: 20 }} />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title={t("add_color")}
        open={colorModalOpen}
        onOk={handleCreateColor}
        onCancel={() => {
          setColorModalOpen(false);
          colorForm.resetFields();
        }}
        okText={t("add")}
        cancelText={t("cancel")}
      >
        <Form form={colorForm} layout="vertical" onFinish={handleCreateColor}>
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="BLACK" />
          </Form.Item>
          <Form.Item name="shadeId" label={t("color_shade")}>
            <Select
              placeholder={t("form.choose_color_shade")}
              showSearch
              optionFilterProp="children"
              filterOption={true}
              options={[...(colorShades || [])]
                .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
                .map((shade) => ({
                  key: shade.id,
                  value: shade.id,
                  label: shade.name,
                }))}
            />
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_color")}
        open={editColorModalOpen}
        onOk={handleUpdateColor}
        onCancel={() => {
          setEditColorModalOpen(false);
          setCurrentColor(null);
          editColorForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
      >
        <Form form={editColorForm} layout="vertical" onFinish={handleUpdateColor}>
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="BLACK" />
          </Form.Item>
          <Form.Item name="shadeId" label={t("color_shade")}>
            <Select
              placeholder={t("form.choose_color_shade")}
              showSearch
              optionFilterProp="children"
              filterOption={true}
              allowClear
              options={[...(colorShades || [])]
                .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
                .map((shade) => ({
                  key: shade.id,
                  value: shade.id,
                  label: shade.name,
                }))}
            />
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("add_material")}
        open={materialModalOpen}
        onOk={handleCreateMaterial}
        onCancel={() => {
          setMaterialModalOpen(false);
          materialForm.resetFields();
        }}
        okText={t("add")}
        cancelText={t("cancel")}
      >
        <Form form={materialForm} layout="vertical" onFinish={handleCreateMaterial}>
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="GLASS" />
          </Form.Item>
          <Form.Item name="groupId" label={t("material_group")}>
            <Select
              placeholder={t("form.choose_material_group")}
              showSearch
              optionFilterProp="children"
              filterOption={true}
              allowClear
              options={[...(materialGroups || [])]
                .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
                .map((group) => ({
                  key: group.id,
                  value: group.id,
                  label: group.name,
                }))}
            />
          </Form.Item>
          <Form.Item name="description" label={t("description")}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_material")}
        open={editMaterialModalOpen}
        onOk={handleUpdateMaterial}
        onCancel={() => {
          setEditMaterialModalOpen(false);
          setCurrentMaterial(null);
          editMaterialForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
      >
        <Form form={editMaterialForm} layout="vertical" onFinish={handleUpdateMaterial}>
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="GLASS" />
          </Form.Item>
          <Form.Item name="groupId" label={t("material_group")}>
            <Select
              placeholder={t("form.choose_material_group")}
              showSearch
              optionFilterProp="children"
              filterOption={true}
              allowClear
              options={[...(materialGroups || [])]
                .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
                .map((group) => ({
                  key: group.id,
                  value: group.id,
                  label: group.name,
                }))}
            />
          </Form.Item>
          <Form.Item name="description" label={t("description")}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("add_color_shade")}
        open={shadeModalOpen}
        onOk={handleCreateShade}
        onCancel={() => {
          setShadeModalOpen(false);
          shadeForm.resetFields();
          setShadeImageFile(null);
        }}
        okText={t("add")}
        cancelText={t("cancel")}
        width={800}
      >
        <Form form={shadeForm} layout="vertical" onFinish={handleCreateShade}>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item name="name" label={t("name_nl")} rules={[{ required: true, message: t("form.enter_value") }]}>
                <Input placeholder="Rood" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameEN"
                label={t("name_en")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input placeholder="Red" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameDE"
                label={t("name_de")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input placeholder="Rot" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
                <Input placeholder="RED" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="sortOrder" label={t("sort_order")}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t("image")}>
            <ImgCrop aspect={1} quality={1}>
              <Upload {...shadeImageUploadProps}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t("upload")}</div>
                </div>
              </Upload>
            </ImgCrop>
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_color_shade")}
        open={editShadeModalOpen}
        onOk={handleUpdateShade}
        onCancel={() => {
          setEditShadeModalOpen(false);
          setCurrentShade(null);
          editShadeForm.resetFields();
          setEditShadeImageFile(null);
        }}
        okText={t("save")}
        cancelText={t("cancel")}
        width={800}
      >
        <Form form={editShadeForm} layout="vertical" onFinish={handleUpdateShade}>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item name="name" label={t("name_nl")} rules={[{ required: true, message: t("form.enter_value") }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameEN"
                label={t("name_en")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameDE"
                label={t("name_de")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
                <Input placeholder="RED" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="sortOrder" label={t("sort_order")}>
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t("image")}>
            <ImgCrop aspect={1} quality={1}>
              <Upload key={currentShade?.id || "new"} {...getEditShadeImageUploadProps()}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t("upload")}</div>
                </div>
              </Upload>
            </ImgCrop>
          </Form.Item>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("add_material_group")}
        open={groupModalOpen}
        onOk={handleCreateGroup}
        onCancel={() => {
          setGroupModalOpen(false);
          groupForm.resetFields();
        }}
        okText={t("add")}
        cancelText={t("cancel")}
        width={800}
      >
        <Form form={groupForm} layout="vertical" onFinish={handleCreateGroup}>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item name="name" label={t("name_nl")} rules={[{ required: true, message: t("form.enter_value") }]}>
                <Input placeholder="Glas" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameEN"
                label={t("name_en")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input placeholder="Glass" />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameDE"
                label={t("name_de")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input placeholder="Glas" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
                <Input placeholder="GLASS" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="sortOrder" label={t("sort_order")}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_material_group")}
        open={editGroupModalOpen}
        onOk={handleUpdateGroup}
        onCancel={() => {
          setEditGroupModalOpen(false);
          setCurrentGroup(null);
          editGroupForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
        width={800}
      >
        <Form form={editGroupForm} layout="vertical" onFinish={handleUpdateGroup}>
          <Row gutter={16}>
            <Col md={8}>
              <Form.Item name="name" label={t("name_nl")} rules={[{ required: true, message: t("form.enter_value") }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameEN"
                label={t("name_en")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={8}>
              <Form.Item
                name="nameDE"
                label={t("name_de")}
                rules={[{ required: true, message: t("form.enter_value") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
                <Input placeholder="GLASS" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="sortOrder" label={t("sort_order")}>
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ display: "none" }}>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
